import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  output,
  inject,
} from '@angular/core';

import { DictionaryDetailsForm } from '../../models/dictionary-details-form';
import { Nillable } from '@web/utils';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  FormControlComponent,
  PrimengButtonDirective,
  PrimengInputTextDirective,
} from '@web/shared/ui';
import { InputTextModule } from 'primeng/inputtext';
import { Button } from 'primeng/button';

type DictionaryDetailsFormControls = {
  label: FormControl<string>;
  slug: FormControl<Nillable<string>>;
  position: FormControl<number>;
};
/* prettier-ignore */
type ControlsPossibleToHide = keyof Pick<DictionaryDetailsFormControls, 'slug'>;
type DictionaryDetailsFormGroup = FormGroup<DictionaryDetailsFormControls>;

@Component({
  selector: 'app-dictionary-details-form',
  imports: [
    ReactiveFormsModule,
    FormControlComponent,
    InputTextModule,
    PrimengInputTextDirective,
    Button,
    PrimengButtonDirective,
  ],
  templateUrl: './dictionary-details-form.component.html',
  styleUrl: './dictionary-details-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DictionaryDetailsFormComponent {
  private readonly builder = inject(FormBuilder);

  public readonly hiddenControls = input<ControlsPossibleToHide[]>([]);
  public readonly data = input<Nillable<DictionaryDetailsForm>>();
  public readonly submitClickEvent = output<DictionaryDetailsForm>();

  protected readonly form = this.buildForm();

  public constructor() {
    effect(() => this.fillByData());
    effect(() => this.hiddenControlsRequirement());
  }

  public handleSubmitClick(): void {
    const data = this.form.getRawValue();

    this.submitClickEvent.emit(
      new DictionaryDetailsForm({
        label: data.label,
        position: data.position,
        slug: data.slug,
      }),
    );
  }

  public shouldBeVisible(control: ControlsPossibleToHide): boolean {
    return !this.hiddenControls().includes(control);
  }

  private buildForm(): DictionaryDetailsFormGroup {
    return this.builder.group({
      label: this.builder.nonNullable.control('', [Validators.required]),
      slug: this.builder.control<Nillable<string>>(''),
      position: this.builder.nonNullable.control(0, [
        Validators.required,
        Validators.min(1),
      ]),
    });
  }

  private fillByData(): void {
    const data = this.data();

    this.form.patchValue({
      label: data?.label,
      slug: data?.slug,
      position: data?.position,
    });
  }

  private hiddenControlsRequirement(): void {
    this.shouldBeVisible('slug')
      ? this.enableSlugControl()
      : this.disableSlugControl();
  }

  private enableSlugControl(): void {
    this.form.controls.slug.setValidators([Validators.required]);
    this.form.controls.slug.updateValueAndValidity();
    this.form.controls.slug.enable();
  }

  private disableSlugControl(): void {
    this.form.controls.slug.clearValidators();
    this.form.controls.slug.updateValueAndValidity();
    this.form.controls.slug.disable();
  }
}
